/**
 * GA4 Analytics — typed helpers that wrap gtag().
 *
 * Usage:  import { trackSignup } from '../lib/analytics'
 *         trackSignup('email')
 *
 * The GA4 measurement ID is read from VITE_GA4_ID at build time.
 * If the env var is missing or gtag hasn't loaded, every call is a safe no-op.
 */

declare global {
  interface Window {
    dataLayer: unknown[]
    gtag: (...args: unknown[]) => void
  }
}

const GA4_ID = import.meta.env.VITE_GA4_ID as string | undefined

/** Initialise GA4 — called once from main.tsx */
export function initGA4(): void {
  if (!GA4_ID) return

  // Inject the gtag.js script
  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`
  document.head.appendChild(script)

  window.dataLayer = window.dataLayer || []
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments)
  }
  window.gtag('js', new Date())
  window.gtag('config', GA4_ID, {
    send_page_view: true,
  })
}

function event(name: string, params?: Record<string, unknown>): void {
  if (typeof window.gtag !== 'function') return
  window.gtag('event', name, params)
}

// ── Auth / Onboarding ───────────────────────────────────────────────

export function trackSignup(method: 'email' | 'otp' | 'google' | 'apple' = 'email'): void {
  event('sign_up', { method })
}

export function trackLogin(method: 'email' | 'otp' | 'password' = 'otp'): void {
  event('login', { method })
}

// ── Team ────────────────────────────────────────────────────────────

export function trackInviteSent(role: string): void {
  event('invite_sent', { role })
}

export function trackTeamMemberJoined(role: string): void {
  event('team_member_joined', { role })
}

// ── Shows ───────────────────────────────────────────────────────────

export function trackShowCreated(source: 'manual' | 'bandsintown' = 'manual'): void {
  event('show_created', { source })
}

export function trackTicketUpdate(showId: string, ticketCount: number): void {
  event('ticket_update', { show_id: showId, ticket_count: ticketCount })
}

export function trackShowSynced(count: number): void {
  event('show_synced', { count })
}

// ── Music ───────────────────────────────────────────────────────────

export function trackSongAdded(source: 'manual' | 'spotify' = 'manual'): void {
  event('song_added', { source })
}

// ── Links ───────────────────────────────────────────────────────────

export function trackLinkCreated(linkType: string): void {
  event('link_created', { link_type: linkType })
}

export function trackLinkClick(linkId: string, linkType: string): void {
  event('link_click', { link_id: linkId, link_type: linkType })
}

export function trackLinkSubmission(linkId: string): void {
  event('link_submission', { link_id: linkId })
}

// ── Money / Finance ─────────────────────────────────────────────────

export function trackMoneyEntry(type: 'revenue' | 'spend', amount: number, source: string): void {
  event('money_entry', { type, amount, source })
}

// ── Tasks ───────────────────────────────────────────────────────────

export function trackTaskCreated(category?: string): void {
  event('task_created', { category })
}

export function trackTaskCompleted(category?: string): void {
  event('task_completed', { category })
}

// ── Chat / AI ───────────────────────────────────────────────────────

export function trackChatMessage(): void {
  event('chat_message')
}

// ── Navigation / Engagement ─────────────────────────────────────────

export function trackPageView(pagePath: string, pageTitle?: string): void {
  if (typeof window.gtag !== 'function') return
  window.gtag('config', GA4_ID, {
    page_path: pagePath,
    page_title: pageTitle,
  })
}

export function trackFeatureUsed(feature: string, detail?: string): void {
  event('feature_used', { feature, detail })
}
